/**
 * "Tie Format" — a second, independent axis from tournament STRUCTURE
 * (round_robin, cross_group, single_elimination, ... — who plays whom).
 * A tie format decides how ONE already-scheduled team-vs-team match resolves
 * into games. It applies uniformly to any generator's output: a round robin
 * tie, a cross-group tie, a bracket match — any match between two 'teams'
 * participants is eligible the same way, via a plain post-processing
 * transform over that format's `rounds` array, not something baked into one
 * generator.
 *
 * MLP (Major League Pickleball style) is the one non-default tie format
 * today: 4 doubles games + a singles Dreambreaker, drawn from each team's
 * own 4-player roster. It only ever applies to a match between two teams
 * with an exactly-4-player roster; anything else stays a single match.
 * Originally ported from `src/tournaments/utils/mlpScheduler.js` (frozen,
 * KYN-475, and shaped for a different DB/live-scoring data model), now
 * generalized further: gender is an optional, per-player, organizer-editable
 * override rather than a required field, and which 4 of a roster's 6
 * possible pairs actually play is organizer-selectable too, not hardcoded to
 * fixed Men's/Women's/Mixed-1/Mixed-2 slots — a doubles game's label is
 * DERIVED from whichever 2 players are actually in it.
 *
 * Graphical (bracket) views never look at `subMatches` — MatchCard only ever
 * renders slotA vs slotB, tie format or not. Only list views expand a tie's
 * `subMatches` into a collapsible detail (see MatchListView.jsx).
 */

export const TIE_FORMATS = {
  SINGLE_MATCH: 'single_match',
  MLP_SLATE: 'mlp_slate',
};

export const MLP_GAME_TYPE_LABELS = {
  dreambreaker: 'Dreambreaker',
};

export const PAIR_ROLE_LABELS = {
  men: "Men's Doubles",
  women: "Women's Doubles",
  mixed: 'Mixed Doubles',
  doubles: 'Doubles', // gender unknown for at least one player in the pair
};

/** A match is MLP-eligible only when BOTH sides are real teams with an exactly-4-player roster. */
export const isMLPEligible = (slotA, slotB) =>
  Array.isArray(slotA?.roster) && slotA.roster.length === 4 && Array.isArray(slotB?.roster) && slotB.roster.length === 4;

/** Every unique 2-player combination from a 4-player roster — C(4,2) = 6 possible doubles pairs. */
export const getAllPairs = (roster) => {
  const pairs = [];
  for (let i = 0; i < roster.length; i++) {
    for (let j = i + 1; j < roster.length; j++) {
      pairs.push([roster[i], roster[j]]);
    }
  }
  return pairs;
};

export const pairKey = (pair) => pair.map((p) => p.id).sort().join('|');

const effectiveGender = (player, genderOverrides) => genderOverrides?.[player.id] || player.gender || null;

/** 'men' | 'women' | 'mixed' | 'doubles' (unspecified) — derived from the pair's actual players, not a fixed slot. */
export const pairRole = (pair, genderOverrides) => {
  const g0 = effectiveGender(pair[0], genderOverrides);
  const g1 = effectiveGender(pair[1], genderOverrides);
  if (!g0 || !g1) return 'doubles';
  if (g0 === 'male' && g1 === 'male') return 'men';
  if (g0 === 'female' && g1 === 'female') return 'women';
  return 'mixed';
};

const ROLE_PRIORITY = { men: 0, women: 1, mixed: 2, doubles: 3 };

/**
 * The 2 pairs (of 6) that sit out by default, when the organizer hasn't
 * customized the selection. With a clean 2-men/2-women roster this
 * reproduces the classic MLP slate exactly: keep the 1 men's pair, the 1
 * women's pair, and 2 non-overlapping mixed pairs (so all 4 players get
 * equal reps) — excluding the other 2 "criss-cross" mixed pairs
 * (men0-women1, men1-women0).
 *
 * Any other gender mix (uneven split, or gender left unset — the common
 * case until an organizer assigns genders via MLP Settings) still needs
 * that exact same SHAPE by default — one "first pair" doubles, one
 * "second pair" doubles, 2 differently-combined pairs — not 4 arbitrary
 * doubles, and not 4 pairs that are all cross-combinations either (an
 * earlier version of this default excluded `roster[0]+roster[1]` and
 * `roster[2]+roster[3]` instead: balanced — every player still in exactly
 * 2 of 4 — but structurally wrong, since excluding BOTH natural pairs
 * leaves nothing playing the "men's doubles"/"women's doubles" role at
 * all, just 4 cross-pairs, which reads as random rather than a real MLP
 * slate). Treating roster[0]/roster[1] as one natural pair and
 * roster[2]/roster[3] as the other and excluding their OWN criss-cross —
 * `roster[0]+roster[3]` and `roster[1]+roster[2]` — mirrors the
 * gender-known branch's exact shape (roster[0..1] standing in for
 * men0/men1, roster[2..3] for women0/women1) without needing to know
 * which two are actually which gender: kept pairs are (0,1), (2,3), (0,2),
 * (1,3) — the same "pair-A doubles + pair-B doubles + 2 mixed" structure,
 * still perfectly balanced (every player in exactly 2).
 */
export const defaultExcludedPairKeys = (roster, genderOverrides) => {
  const men = roster.filter((p) => effectiveGender(p, genderOverrides) === 'male');
  const women = roster.filter((p) => effectiveGender(p, genderOverrides) === 'female');
  if (men.length === 2 && women.length === 2) {
    return [pairKey([men[0], women[1]]), pairKey([men[1], women[0]])];
  }
  return [pairKey([roster[0], roster[3]]), pairKey([roster[1], roster[2]])];
};

/** The 4 pairs that actually play, sorted men/women/mixed/doubles so same-role pairs line up across the net. */
const resolvePlayingPairs = (roster, genderOverrides, excludedKeys) =>
  getAllPairs(roster)
    .filter((pair) => !excludedKeys.includes(pairKey(pair)))
    .map((pair) => ({ pair, role: pairRole(pair, genderOverrides) }))
    .sort((a, b) => ROLE_PRIORITY[a.role] - ROLE_PRIORITY[b.role]);

/**
 * Builds one tie's game slate: 4 doubles games (from each side's 4 selected
 * pairs, matched up in role order) + 1 singles Dreambreaker (always the
 * roster's first player — not affected by pair selection, since it's not a
 * doubles game). `overrides` is keyed by team id:
 * `{ [teamId]: { genders: {[playerId]: 'male'|'female'}, excludedPairKeys: [k1, k2] } }`.
 */
export const buildMLPGames = (teamA, teamB, overrides = {}) => {
  const gendersA = overrides[teamA.id]?.genders || {};
  const gendersB = overrides[teamB.id]?.genders || {};
  const excludedA = overrides[teamA.id]?.excludedPairKeys || defaultExcludedPairKeys(teamA.roster, gendersA);
  const excludedB = overrides[teamB.id]?.excludedPairKeys || defaultExcludedPairKeys(teamB.roster, gendersB);

  const pairsA = resolvePlayingPairs(teamA.roster, gendersA, excludedA);
  const pairsB = resolvePlayingPairs(teamB.roster, gendersB, excludedB);

  const doublesGames = pairsA.map((pa, i) => {
    const pb = pairsB[i];
    const role = pb && pa.role === pb.role ? pa.role : 'doubles';
    return {
      gameType: `doubles_${i + 1}`,
      gameTypeLabel: PAIR_ROLE_LABELS[role],
      playersA: pa.pair,
      playersB: pb ? pb.pair : [],
    };
  });

  const dreambreaker = {
    gameType: 'dreambreaker',
    gameTypeLabel: MLP_GAME_TYPE_LABELS.dreambreaker,
    playersA: teamA.roster[0] ? [teamA.roster[0]] : [],
    playersB: teamB.roster[0] ? [teamB.roster[0]] : [],
  };

  return [...doublesGames, dreambreaker];
};

/**
 * Whether ANY match across the given rounds arrays is MLP-eligible — used to
 * decide whether a format's result view should even offer the Tie Format
 * toggle. `roundsArrays` is an array of `rounds` arrays, since some formats
 * expose more than one (e.g. a group stage's rounds + a knockout's rounds).
 */
export const hasEligibleTies = (roundsArrays) =>
  roundsArrays.some((rounds) => (rounds || []).some((r) => r.matches.some((m) => isMLPEligible(m.slotA, m.slotB))));

/**
 * Every distinct MLP-eligible team appearing across the given rounds arrays
 * — the roster/pairing config panel's team list. Takes the same
 * `roundsArrays` shape as `hasEligibleTies` (an array of `rounds` arrays),
 * since a format can expose matches across more than one (per-region,
 * per-direction, group-stage + knockout, ...).
 */
export const collectEligibleTeams = (roundsArrays) => {
  const byId = new Map();
  (roundsArrays || []).forEach((rounds) => {
    (rounds || []).forEach((round) => {
      round.matches.forEach((match) => {
        if (isMLPEligible(match.slotA, match.slotB)) {
          byId.set(match.slotA.id, match.slotA);
          byId.set(match.slotB.id, match.slotB);
        }
      });
    });
  });
  return Array.from(byId.values());
};

/**
 * Every distinct "rounds" array a schedule can contain, format by format —
 * needed because each generator shapes its output differently (a flat
 * `rounds`, per-region `regions[].rounds` + `finalStage`, per-direction
 * `groups[dir].rounds`, or a pre-merged `executionOrder` for the two HYBRID
 * formats that interleave stages — see groupKnockoutGenerator.js /
 * pagePlayoffGenerator.js, both of which build `executionOrder` as the
 * complete match set already, so it alone is enough here).
 *
 * MLP eligibility is a property of individual matches (two 4-player team
 * rosters facing off), not of which structure format scheduled them — this
 * is what lets `hasEligibleTies`/`collectEligibleTeams` look across ANY
 * format's schedule the same way, regardless of its resultView.
 */
export const getScheduleRoundsArrays = (format, schedule) => {
  if (!schedule) return [];
  if (format.id === 'compass_draw') {
    return (schedule.activeDirections || []).map((dir) => schedule.groups?.[dir]?.rounds).filter(Boolean);
  }
  if (format.id === 'regional_bracket') {
    return [...(schedule.regions || []).map((r) => r.rounds), schedule.finalStage].filter(Boolean);
  }
  if (format.id === 'group_knockout' || format.id === 'page_playoff') {
    return [schedule.executionOrder].filter(Boolean);
  }
  // Ladder never has team rosters (format.disallowsTeams — see
  // formatEligibility.js), so this never actually surfaces an MLP-eligible
  // tie — added for the Filters panel's benefit (see scheduleRows.js's
  // filter-option collection), which needs every one of a schedule's rounds
  // regardless of format, not just the ones MLP eligibility cares about.
  if (format.id === 'ladder') {
    return (schedule.ladderRounds || []).flatMap((lr) => (lr.courts || []).map((c) => c.rounds)).filter(Boolean);
  }
  return [schedule.rounds].filter(Boolean);
};

/**
 * Applies a tie format to one `rounds` array, returning a NEW array —
 * doesn't mutate the generator's original output. SINGLE_MATCH strips any
 * `subMatches` (the default, and what every non-eligible match already looks
 * like); MLP_SLATE attaches the game slate to every eligible match (using
 * `overrides` for gender/pairing customization) and leaves ineligible ones
 * untouched.
 */
export const applyTieFormatToRounds = (rounds, tieFormat, overrides = {}) =>
  (rounds || []).map((round) => ({
    ...round,
    matches: round.matches.map((match) => {
      // `match.gameType` set means this match IS ALREADY one specific
      // discipline of a tie — real, saved, and loaded back from the
      // backend (see scheduleApi.js's hydrateFlatRounds) as its own row,
      // not a raw unresolved tie. Re-expanding it here would double it
      // into its own 5-game subMatches on top of the 5 real rows the
      // backend already gave us for that tie. A raw tie (fresh from a
      // generator, never saved) never has `gameType` set, so this only
      // ever excludes already-resolved discipline rows.
      if (tieFormat !== TIE_FORMATS.MLP_SLATE || match.gameType || !isMLPEligible(match.slotA, match.slotB)) {
        if (!match.subMatches) return match;
        const { subMatches, ...rest } = match;
        return rest;
      }
      return { ...match, subMatches: buildMLPGames(match.slotA, match.slotB, overrides) };
    }),
  }));
